import { hasOwnProperty } from "@glideapps/ts-necessities";
import { isLeft, isRight } from "fp-ts/lib/Either";
import * as t from "io-ts";
import { appPlanCodeCodec, SubscriptionType } from "./app-plans";
import { baseThemeCodec } from "./BaseTheme";
import { boostCodeCodec } from "./boosts";
import { appKindCodec } from "./components/app-kind";
import { iconImageCodec } from "./components/icon-image";
import { appAuthenticationCodec, appDescriptionCodec } from "./components/SerializedApp";
import { ActionKind, tableNameCodec } from "./database-strings";
import type { StripeCardInfo, StripeDate } from "./Database/stripe-info";
import { logError } from "./debug-print";
import type { TypeSchema } from "./description";
import { paymentProcessorCodec, tableColumnCodec, typeSchemaCodec } from "./description";
import { sourceMetadataCodec } from "./source-metadata";
import { panic } from "./Support";

// FIXME: Shouldn't we use `t.exact` on most types?

// This originally came from the backend, but we are now using it as direct
// responses to login requests, as a replacement for the old password mecahnism.
export const aeadDataCodec = t.intersection([
    t.type({
        iv: t.string,
        data: t.string,
        tag: t.string,
    }),
    t.partial({
        authOnlyData: t.string,
        overrideEmail: t.string,
    }),
]);
export const appLoginTokenContainerCodec = aeadDataCodec;
export type AppLoginTokenContainer = t.TypeOf<typeof appLoginTokenContainerCodec>;

export const aeadKeyedDataCodec = t.intersection([aeadDataCodec, t.partial({ keyName: t.string })]);
export type AeadKeyedData = t.TypeOf<typeof aeadKeyedDataCodec>;
export const possiblyEncryptedString = t.union([aeadKeyedDataCodec, t.string]);

export const gcpSavedServiceAccountSchema = t.type({
    projectID: t.string,
    privateKeyID: t.string,
    privateKey: possiblyEncryptedString,
    clientEmail: t.string,
    clientID: t.string,
    authURI: t.string,
    tokenURI: t.string,
    authProviderX509CertURL: t.string,
    clientX509CertURL: t.string,
});
export type GCPSavedServiceAccountCredentials = t.TypeOf<typeof gcpSavedServiceAccountSchema>;

export function asAppLoginTokenContainer(x: unknown): AppLoginTokenContainer | undefined {
    const decoded = appLoginTokenContainerCodec.decode(x);
    return isLeft(decoded) ? undefined : decoded.right;
}

export const appIDBody = t.type({
    appID: t.string,
});

export const passwordBody = t.intersection([
    appIDBody,
    t.partial({
        // Note that this used to be required, but is being deprecated
        // by the new "token" field.
        // FIXME: Remove this field once app login passwords are removed
        password: t.string,
        // FIXME: Validate that it's an email address
        email: t.string,
        // This is a general replacement for the old email/password app login
        // system. We'll need to gradually roll this out.
        token: appLoginTokenContainerCodec,
    }),
]);

const newAppSurveyData = t.type({
    sourceUsed: t.string,
    sourceWanted: t.array(t.string),
    formFactor: t.string,
});

export const generatePublishedAppDataFromSheetBody = t.intersection([
    newAppSurveyData,
    t.type({
        sheetsFileID: t.string,
        appKind: appKindCodec,
    }),
    t.partial({
        organizationID: t.string,
    }),
]);

// FIXME: finish
const typeSchema = t.unknown;

const generatePublishedAppDataFromSheetResult = t.intersection([
    appIDBody,
    t.type({
        sourceMetadata: sourceMetadataCodec,
        schema: typeSchema,
    }),
    t.partial({
        spreadsheetName: t.string,
        numRowsUsedInApp: t.number,
    }),
]);

const generatePublishedAppDataFromExcelResult = t.intersection([
    appIDBody,
    t.type({
        sourceMetadata: t.array(sourceMetadataCodec),
        schema: typeSchema,
    }),
    t.partial({
        spreadsheetName: t.string,
        numRowsUsedInApp: t.number,
    }),
]);

export const generatePublishedAppDataFromDataBody = t.type({
    organizationID: t.string,
    schema: typeSchema,
    tableData: t.record(t.string, t.readonlyArray(t.record(t.string, t.unknown))),
});

export const getAIPlaygroundImages = t.type({
    imageQueries: t.readonlyArray(t.string),
});

export const generateAICompletionBody = t.type({
    userPrompt: t.string,
    userID: t.string,
});

const generatePublishedAppDataFromDataResponse = t.intersection([
    appIDBody,
    t.type({
        sourceMetadata: t.readonlyArray(sourceMetadataCodec),
        schema: typeSchema,
    }),
]);

export const prepareReplaceGoogleSheetsSourceBody = t.intersection([
    appIDBody,
    t.type({
        replacementFileID: t.string,
    }),
    t.partial({
        // The function doesn't actually use this anymore, other than for
        // logging.
        originalFileID: t.string,
    }),
]);

export const prepareReplaceGoogleSheetsSourceResponse = t.union([
    t.type({
        status: t.literal("ok"),
        schema: typeSchema,
        title: t.string,
    }),
    t.type({
        status: t.literal("shared-drive-needs-upgrade"),
    }),
]);

export const reloadPublishedAppDataFromSheetBody = t.intersection([
    appIDBody,
    t.type({
        checkCompatibilityIssues: t.boolean,
    }),
    t.partial({
        didReconnectSpreadsheet: t.boolean,
    }),
]);

export const generateAppFromDescriptionBody = t.intersection([
    appIDBody,
    t.type({
        // Unless `force` is set, we won't publish
        // the app if it isn't already published. This is a
        // safeguard so we don't accidentally publish an app
        // that shouldn't be.
        force: t.boolean,
    }),
]);

const addUserProfileRowBody = t.partial({
    addUserProfileRow: t.boolean,
    // FIXME: This is only partial to be compatible with older frontends.
    deviceID: t.string,
});

export const getCustomTokenForAppBody = t.intersection([passwordBody, addUserProfileRowBody]);

export const authorizeUserForAppBody = t.intersection([passwordBody, addUserProfileRowBody]);

export const getAppUserForAuthenticatedUserBody = appIDBody;

export const sendPinForEmailBody = t.intersection([
    appIDBody,
    t.type({ email: t.string }),
    t.partial({ glideCommit: t.string }),
]);

export const getPasswordForEmailPinBody = t.intersection([
    appIDBody,
    t.type({
        // FIXME: Validate that it's an email address
        email: t.string,
        pin: t.string,
    }),
    t.partial({
        userAgreed: t.boolean,
    }),
    addUserProfileRowBody,
]);

export const getPasswordForOAuth2TokenBody = t.intersection([
    appIDBody,
    t.type({
        authToken: t.string,
        userAgreed: t.boolean,
    }),
    addUserProfileRowBody,
]);

export const sendShareSMSBody = t.intersection([
    appIDBody,
    t.type({
        // FIXME: Validate that it's a phone number
        phoneNumber: t.string,
    }),
]);

export const getUnsplashImagesBody = t.intersection([
    appIDBody,
    t.type({
        query: t.string,
    }),
]);

export const unsplashPingDownloadBody = t.intersection([
    appIDBody,
    t.type({
        imageId: t.string,
    }),
]);

export const registerForPushNotificationsBody = t.intersection([
    appIDBody,
    t.type({
        appUserID: t.string,
        registrationToken: t.string,
    }),
]);

export const deleteAppBody = appIDBody;

const userFlags = t.type({
    hasSeenOrganizeTip: t.boolean,
    hasSeenNavigationTip: t.boolean,
    onboardingComplete: t.boolean,
    tourComplete: t.boolean,
    filterWarningComplete: t.boolean,
    visibilityWarningComplete: t.boolean,
    unprotectedColumnWarningComplete: t.boolean,
    allowEmail: t.boolean,
    disabledNewComponentsForNewColumns: t.boolean,
    disabledLinkTablesModal: t.boolean,
    // This isn't strictly a flag, but... whatever.
    lastAcceptedTermsOfService: t.number,
    builderTheme: t.union([t.literal("light"), t.literal("dark")]),
    useSystemTheme: t.boolean,
    // This flag is pretty loaded in terms of meaning.
    // It controls both auth persistence and analytics in the builder,
    // unless the user feature builderTrackingDisableAnalytics = true,
    // then it only controls auth persistence.
    builderTrackingOptOut: t.boolean,
    hidePagesAdvert: t.boolean,
});

export type UserFlags = Readonly<t.TypeOf<typeof userFlags>>;

const oAuth2OfflineAuthentication = t.type({
    kind: t.literal("oauth2-offline"),
    idToken: t.string,
    refreshToken: t.string,
});

export type OAuth2OfflineAuthentication = t.TypeOf<typeof oAuth2OfflineAuthentication>;

const googleDriveIntegration = t.type({
    name: t.string,
    kind: t.literal("google-drive"),
    authentication: oAuth2OfflineAuthentication,
});

export type GoogleDriveIntegration = t.TypeOf<typeof googleDriveIntegration>;

const stripeGenericIntegration = t.intersection([
    t.type({
        name: t.string,
        stripeUserID: t.string,
        authentication: oAuth2OfflineAuthentication,
    }),
    t.partial({
        // These would be mandatory, but we started the closed Beta without them.
        livePublishableKey: t.string,
        testPublishableKey: t.string,
        // This would be readonly, but we need to establish it only if it
        // is defined, because of Cloud Firestore and its bizzare treatment of `undefined`.
        stripeEmail: t.string,
        waiveTransactionFees: t.boolean,
    }),
]);

export const microsoftTokens = t.type({
    username: t.string,
    accessToken: t.string,
    refreshToken: t.string,
});

export const microsoftIntegrationCodec = t.intersection([
    t.type({ name: t.string, kind: t.literal("microsoft") }),
    microsoftTokens,
]);
export type MicrosoftTokens = t.TypeOf<typeof microsoftTokens>;
export type MicrosoftIntegration = t.TypeOf<typeof microsoftIntegrationCodec>;

export const airtableTokens = t.type({
    accessToken: t.string,
    userEmail: t.string,
});
export type AirtableTokens = t.TypeOf<typeof airtableTokens>;

export const airtableIntegrationCodec = t.intersection([
    t.type({ name: t.string, kind: t.literal("airtable") }),
    airtableTokens,
]);

export type AirtableIntegration = t.TypeOf<typeof airtableIntegrationCodec>;

export const gcpIntegrationCodec = t.type({
    name: t.string,
    kind: t.literal("gcp-service-account"),
    serviceAccount: gcpSavedServiceAccountSchema,
});

export type GCPIntegration = t.TypeOf<typeof gcpIntegrationCodec>;

const stripeIntegration = t.intersection([t.type({ kind: t.literal("stripe") }), stripeGenericIntegration]);
export type StripeIntegration = t.TypeOf<typeof stripeIntegration>;

const stripeTemplateStoreIntegration = t.intersection([
    t.type({ kind: t.literal("stripe-template-store") }),
    stripeGenericIntegration,
]);
export type StripeTemplateStoreIntegration = t.TypeOf<typeof stripeTemplateStoreIntegration>;

const integration = t.union([
    googleDriveIntegration,
    stripeIntegration,
    stripeTemplateStoreIntegration,
    microsoftIntegrationCodec,
    airtableIntegrationCodec,
    gcpIntegrationCodec,
]);

export type Integration = t.TypeOf<typeof integration>;

const reasonForGlide = t.union([t.literal("fun"), t.literal("other"), t.literal("work"), t.literal("school")]);

const selfReportedReferralSource = t.union([
    t.literal("google-search"),
    t.literal("word-of-mouth"),
    t.literal("social-media"),
    t.literal("blog-or-publication"),
    t.literal("other"),
]);

const userDataUpdates = t.partial({
    email: t.string,
    displayName: t.string,
    phoneNumber: t.string,
    photoURL: t.string,
    flags: t.partial(userFlags.props),
    integrations: t.readonlyArray(integration),
    reasonForGlide: reasonForGlide,
    reasonForGlideOtherExplanation: t.string,
    selfReportedReferralSource: selfReportedReferralSource,
    selfReportedReferralSourceOtherExplanation: t.string,
    partnerStackReferralPartnerKey: t.string,
});

export type UserDataUpdates = t.TypeOf<typeof userDataUpdates>;

const userDataRemovals = t.partial({
    integrations: t.readonlyArray(integration),
});

export type UserDataRemovals = t.TypeOf<typeof userDataRemovals>;

export const setOrUpdateUserBody = t.intersection([
    t.type({
        updates: userDataUpdates,
        removals: userDataRemovals,
    }),
    t.partial({
        referredBy: t.string,
    }),
]);

export const isEmailSignedUpBody = t.type({
    email: t.string,
});

export const setAppPlanBody = t.intersection([
    appIDBody,
    t.type({
        plan: t.keyof({ free: null, pro: null }),
    }),
]);

export const setAppPlanUnifiedBody = t.intersection([
    appIDBody,
    t.type({
        plan: appPlanCodeCodec,
    }),
    t.partial({
        attachPaymentMethodAsDefault: t.union([t.string, t.literal(false)]),
        numUsers: t.number,
    }),
]);

export const previewChargeBody = t.partial({
    // do not rename this property unless it matches organizationBody
    organizationID: t.string,
    withChange: setAppPlanUnifiedBody,
});
export type PreviewChargeBody = t.TypeOf<typeof previewChargeBody>;

export const legacyUpgradeBody = t.intersection([
    appIDBody,
    t.type({
        action: t.union([t.literal("go-annual"), t.literal("renew-now")]),
    }),
]);
export type LegacyUpgradeBody = t.TypeOf<typeof legacyUpgradeBody>;

const cardPreview = t.type({
    last4: t.string,
    address: t.type({
        line1: t.string,
        line2: t.string,
        country: t.string,
        state: t.string,
        city: t.string,
        zip: t.string,
    }),
    email: t.string,
    name: t.string,
});
export type CardPreview = t.TypeOf<typeof cardPreview>;

const chargeResult = t.intersection([
    t.type({
        amountDue: t.number,
        cardPreview,
        nextDue: t.type({
            day: t.number,
            month: t.number,
            year: t.number,
        }),
    }),
    t.partial({ currentBalance: t.number }),
]);
export type ChargeResult = t.TypeOf<typeof chargeResult>;

export const previewChargeResponse = t.partial({
    immediate: chargeResult,
    monthly: chargeResult,
    annual: chargeResult,
    cardInfo: cardPreview,
    portalUrl: t.string,
});
export type PreviewChargeResponse = t.TypeOf<typeof previewChargeResponse>;

export const getOAuth2TokensForGoogleSheetsBody = t.partial({
    code: t.string,
});

export const duplicateAppBody = t.intersection([
    appIDBody,
    t.type({
        copySheet: t.boolean,
        // There are two ways this function gets called:
        // 1. From the dashboard, clicking "Duplicate" in the
        //    app menu.
        // 2. From the "Copy this app" button in the player.
        //
        // In case 1, the app is to be copied to wherever the
        // original was, My Apps vs an org.  In case 2, the app
        // always has to go to My Apps.  This flag is `true`
        // for case 2.
        fromTemplate: t.boolean,
    }),
    t.partial({
        dataOnly: t.boolean,
    }),
]);

export const duplicateAppResponseBody = t.intersection([
    t.type({
        appID: t.string,
    }),
    t.partial({
        sourceMetadata: t.readonlyArray(sourceMetadataCodec),
        schema: typeSchemaCodec,
        title: t.string,
    }),
]);

export const submitTemplateBody = t.intersection([
    appIDBody,
    t.type({
        name: t.string,
        author: t.string,
        description: t.string,
        category: t.string,
        price: t.number,
    }),
    t.partial({
        usageVideoURL: t.string,
    }),
]);

export const organizationBody = t.type({
    organizationID: t.string,
});

export const setOrgAdminBody = t.intersection([
    organizationBody,
    t.type({
        userIDOrEmail: t.string,
    }),
]);
export type SetOrgAdminBody = t.TypeOf<typeof setOrgAdminBody>;

const range = t.type({
    min: t.number,
    max: t.number,
});

const companyInformation = t.type({
    industryKind: t.string,
    numEmployees: range,
    numAppEditors: range,
    numAppUsers: range,
});

export type CompanyInformation = t.TypeOf<typeof companyInformation>;

export const createOrganizationBody = t.type({
    displayName: t.string,
    inviteeEmails: t.readonlyArray(t.string),
    companyInformation,
});

export const setOrgOnboardingBody = t.intersection([
    organizationBody,
    t.type({
        displayName: t.string,
        companyInformation,
    }),
]);

export const createOrUpdateHubspotContactBody = t.intersection([
    t.type({
        displayName: t.string,
        reasonForGlide: t.string,
        isForWork: t.boolean,
        selfReportedReferralSource: selfReportedReferralSource,
    }),
    t.partial({
        company: t.string,
        lineOfWork: t.string,
        companySize: t.string,
        reasonForGlideOtherExplanation: t.string,
        selfReportedReferralSourceOtherExplanation: t.string,
        partnerStackReferralPartnerKey: t.string,
    }),
]);

export const finishOnboardingBody = organizationBody;

const organizationMember = t.intersection([
    t.type({
        userID: t.string,
        role: t.union([t.literal("admin"), t.literal("editor")]),
        status: t.string,
    }),
    t.partial({
        displayName: t.string,
        email: t.string,
        photoURL: t.string,
    }),
]);

export type OrganizationMember = t.TypeOf<typeof organizationMember>;

const getOrganizationMembersResult = t.type({
    members: t.array(organizationMember),
});

const createOrganizationResult = t.intersection([
    t.type({
        organizationID: t.string,
    }),
    getOrganizationMembersResult,
]);

export const getOrganizationMembersBody = organizationBody;

export const getOrganizationBillingBody = organizationBody;

export const getOrganizationPlansBody = organizationBody;

export const getOrganizationBillingResult = t.type({
    canUpgrade: t.boolean,

    basePrice: t.number,
    pricePerUser: t.number,
    pricePerApp: t.number,
    totalPrice: t.number,

    nextBillDate: t.number,

    publicAppIDs: t.array(t.string),
    includedUsers: t.number,
    includedApps: t.number,
    additionalUsers: t.number,
});

export const inviteAppUserBody = t.intersection([
    appIDBody,
    t.type({
        inviteeEmail: t.string,
    }),
]);

export const getOrganizationPersistentInviteLinkBody = t.type({
    organizationID: t.string,
    createNew: t.boolean,
});

export const getOrganizationPersistentInviteLinkResult = t.type({
    persistentInviteLink: t.string,
});

export const acceptInviteBody = t.type({
    acceptCode: t.string,
});

export const inviteToOrganizationBody = t.intersection([
    organizationBody,
    t.type({
        inviteeEmails: t.readonlyArray(t.string),
    }),
]);

const inviteToOrganizationResult = getOrganizationMembersResult;

export const renameOrganizationBody = t.intersection([
    organizationBody,
    t.type({
        newName: t.string,
    }),
]);

export const removeFromOrganizationBody = t.intersection([
    organizationBody,
    t.type({
        userID: t.string,
        userEmail: t.string,
    }),
]);

export const renameTableBody = t.intersection([
    appIDBody,
    t.type({
        tableName: tableNameCodec,
        newName: t.string,
    }),
]);

export const deleteOrganizationBody = organizationBody;

export const transferAppToOrganizationBody = t.intersection([
    organizationBody,
    appIDBody,
    t.type({ confirmed: t.boolean, asPublicApp: t.boolean }),
]);

export enum TransferAppToOrganizationResponse {
    Success = "success",
    ErrorCannotTransferOrgApp = "error-cannot-transfer-org-app",
    ConfirmTransferUnpublished = "confirm-transfer-unpublished",
    ErrorCannotTransferPublishedToFree = "error-cannot-transfer-published-to-free",
    ErrorCannotTransferPaidToFreeOrg = "error-cannot-transfer-paid-to-free-org",
    ErrorCannotTransferTemplateBasedApp = "error-cannot-transfer-template-based-app",
    ConfirmTransferPublished = "confirm-transfer-published",
    ErrorCannotTransferBetweenUnifiedAndNon = "error-cannot-transfer-between-unified-and-non",
    ErrorOther = "error-other",
}

export const setEmailOwnersColumnsBody = t.intersection([
    appIDBody,
    t.type({
        // updates for email owners
        updates: t.record(t.string, t.readonlyArray(t.string)),
    }),
]);

export const modifySyntheticColumnsBody = t.intersection([
    appIDBody,
    t.partial({
        set: t.record(t.string, t.readonlyArray(tableColumnCodec)),
        remove: t.record(t.string, t.readonlyArray(t.string)),
        // table -> [column, index][]
        move: t.record(t.string, t.readonlyArray(t.tuple([t.string, t.number]))),
        // table -> [column, isProtected][]
        setProtected: t.record(t.string, t.readonlyArray(t.tuple([t.string, t.boolean]))),
        rowIDColumns: t.readonlyArray(t.tuple([t.string, t.union([t.string, t.null])])),
        confirmDestructive: t.boolean,
    }),
]);

export const moveTableInSchemaBody = t.intersection([
    appIDBody,
    t.type({
        tableName: tableNameCodec,
    }),
    t.partial({
        afterTableName: tableNameCodec,
    }),
]);

export const moveOrgInUserBody = t.intersection([
    t.type({
        orgID: t.string,
    }),
    t.partial({
        afterOrgID: t.string,
    }),
]);

export const createAppFromTemplateBody = t.intersection([
    t.type({
        sourceAppID: t.string,
        appDescription: appDescriptionCodec,
    }),
    t.partial({
        ownerID: t.string,
    }),
]);

export const broadcastNotificationToAppUsersBody = t.intersection([
    appIDBody,
    t.type({
        subject: t.string,
    }),
    t.partial({
        body: t.string,
    }),
]);

export const transferAppBetweenUsersBody = t.type({
    appNameOrID: t.string,
    fromUserIDOrEmail: t.string,
    toUserIDOrEmail: t.string,
});

export const reportGeocodesInAppBody = t.intersection([
    appIDBody,
    t.type({
        geocodes: t.number,
    }),
]);

export const reportQuotaIncreaseBody = t.intersection([
    appIDBody,
    t.type({ quotaChanges: t.record(t.string, t.number) }),
]);

export const checkDomainConfiguredBody = t.intersection([
    t.type({
        domain: t.string,
        user: t.string,
    }),
    t.partial({
        skipCheck: t.boolean,
    }),
]);

export const addTableBody = t.intersection([
    appIDBody,
    t.type({
        target: t.union([t.literal("native"), t.literal("gsheet")]),
    }),
]);

const nativeTableBody = t.type({
    target: t.literal("native"),
    nativeTableID: t.string,
});

const nativeTableAppIDBody = t.intersection([appIDBody, nativeTableBody]);
export const removeTableBody = nativeTableAppIDBody;
export const duplicateTableBody = nativeTableAppIDBody;

const nativeTablesBody = t.type({
    target: t.literal("native"),
    nativeTableIDs: t.readonlyArray(t.string),
});

export type NativeTablesBody = t.TypeOf<typeof nativeTablesBody>;

const excelDataSource = t.literal("excel-online");
const airtableDataSource = t.literal("airtable");
const mysqlGcpDataSource = t.literal("mysql-gcp");
// This is a really crappy hack. I agree.
// It should never be actually used on the backend;
// those need to have actual defined names.
// Ask me why it exists some time. -Dan
const unknownDataSource = t.literal("unknown");

export const syncedDataSources = t.union([excelDataSource, airtableDataSource, mysqlGcpDataSource, unknownDataSource]);

export type SyncedDataSources = t.TypeOf<typeof syncedDataSources>;
export function isValidSyncedDataSource(x: unknown): x is SyncedDataSources {
    return isRight(syncedDataSources.decode(x));
}

const syncedDataCollectionID = t.union([
    t.type({
        target: excelDataSource,
        id: t.string,
        account: t.string,
    }),
    t.type({
        target: airtableDataSource,
        id: t.string,
    }),
]);

const nativeTablesAppIDBody = t.intersection([appIDBody, t.union([nativeTablesBody, syncedDataCollectionID])]);
export const linkTablesBody = nativeTablesAppIDBody;
export const syncTablesBody = t.intersection([appIDBody, nativeTablesBody]);

export const deleteTableBody = t.type({
    target: t.literal("native"),
    nativeTableID: t.string,
    editingAppID: t.string,
});

export type DataSourceChoice = "glide" | "google" | SyncedDataSources;

export type FutureDataSourceLink = {
    dataSource: FutureDataSource;
    link: string;
    subtitle?: string;
};

export const futureDataSourceLinks: FutureDataSourceLink[] = [
    { dataSource: "AirTable", link: "/images/airtable.png" },
    { dataSource: "MySQL", link: "/images/mysql.png" },
    { dataSource: "Excel", link: "/images/excel.png" },
    { dataSource: "CSV", link: "/images/csv.png" },
    { dataSource: "Salesforce", link: "/images/salesforce.png" },
    { dataSource: "BigQuery", link: "/images/bigquery.png" },
    { dataSource: "Redshift", link: "/images/redshift.png" },
    { dataSource: "GraphQL", link: "/images/graphql.png" },
    { dataSource: "Workday", link: "/images/workday.png" },
];

export const syncedSourceTitleText: Record<SyncedDataSources, string> = {
    "excel-online": "Excel Workbook",
    airtable: "Airtable",
    "mysql-gcp": "MySQL (Google Cloud SQL)",
    unknown: "Unknown External Source",
};

export const listTablesBody = t.partial({
    organizationID: t.string,
    externalDataSource: syncedDataSources,
});

const nativeTableInfo = t.type({
    id: t.string,
    displayName: t.string,
    connectedApps: t.readonlyArray(t.string),
    numRows: t.number,
    lastModified: t.number, // JS date
});
export type NativeTableInfo = t.TypeOf<typeof nativeTableInfo>;

export const listTablesResult = t.type({
    nativeTables: t.readonlyArray(nativeTableInfo),
});

export const checkDomainRecord = t.intersection([
    t.type({
        host: t.string,
        type: t.string,
        value: t.string,
        expected: t.string,
    }),
    t.partial({
        error: t.string,
    }),
]);

export const checkDomainConfiguredResult = t.intersection([
    t.type({
        result: t.string,
        records: t.readonlyArray(checkDomainRecord),
    }),
    t.partial({
        errorType: t.string,
        errorMessage: t.string,
    }),
]);

export const setCustomDomainBody = t.intersection([
    appIDBody,
    t.type({
        domain: t.string,
    }),
]);

export const setShortNameBody = t.intersection([
    appIDBody,
    t.type({
        shortName: t.string,
    }),
]);

export const setPublishingActiveBody = t.intersection([
    appIDBody,
    t.type({
        active: t.boolean,
    }),
]);
export const setPublishingActiveResponseBody = t.type({
    active: t.boolean,
});

export const discardPausedAppChangesBody = appIDBody;

export const baseRowIndexCodec = t.union([t.number, t.string]);
export type BaseRowIndex = t.TypeOf<typeof baseRowIndexCodec>;

export function isBaseRowIndex(x: unknown): x is BaseRowIndex {
    return typeof x === "number" || typeof x === "string";
}

export function checkBaseRowIndex(x: unknown): BaseRowIndex {
    if (!isBaseRowIndex(x)) {
        logError("Invalid row index", x);
        return panic("Row index must be a string or a number");
    }
    return x;
}

// FIXME: This isn't used anymore.
export const acquireStripeSessionBody = t.intersection([
    appIDBody,
    t.type({
        plan: t.keyof({ pro: null }),
        billingPlan: t.string,
        paidURL: t.string,
        canceledURL: t.string,
    }),
]);

const acquireStripeSessionCommonBody = t.intersection([
    appIDBody,
    t.type({
        paidURL: t.string,
        canceledURL: t.string,
    }),
    t.partial({
        idempotencyKey: t.string,
    }),
]);

export const acquireStripeSessionForProPurchaseBody = t.intersection([
    acquireStripeSessionCommonBody,
    t.type({
        idempotencyKey: t.string,
        plan: t.union([t.literal("pro-annual"), t.literal("pro-monthly")]),
    }),
]);

export const acquireStripeSessionForTemplatePurchaseBody = t.intersection([
    acquireStripeSessionCommonBody,
    t.partial({
        targetOwnerID: t.string,
    }),
]);

export const setBoostsForOwnerBody = t.intersection([
    t.type({
        ownerID: t.string,
        addBoostCodes: t.array(boostCodeCodec),
        removeBoostCodes: t.array(boostCodeCodec),
    }),
    t.partial({
        attachPaymentMethodAsDefault: t.union([t.string, t.literal(false)]),
    }),
]);

export const updateUnifiedPaymmentInformationBody = t.intersection([
    t.type({ ownerID: t.string }),
    t.partial({ paymentMethodID: t.union([t.string, t.literal(false)]) }),
]);

export const integrateWithStripeBody = t.union([
    t.intersection([
        t.type({
            postConnectURL: t.string,
        }),
        t.partial({
            onBehalfOf: t.string,
            forTemplateStore: t.boolean,
            closeOnSuccess: t.boolean,
        }),
    ]),
    t.type({
        postConnectURL: t.undefined,
        token: t.string,
        authorization: t.string,
    }),
    t.intersection([
        t.type({
            postConnectURL: t.undefined,
            token: t.undefined,
            disconnect: t.literal(true),
        }),
        t.partial({
            onBehalfOf: t.string,
            forTemplateStore: t.boolean,
        }),
    ]),
]);

export const integrateWithMicrosoftRequestBody = t.union([
    t.type({ kind: t.literal("request") }),
    t.type({
        kind: t.literal("response"),
        token: t.string,
        code: t.string,
        viaURL: t.string,
    }),
]);

export const integrateWithMicrosoftResponseBody = t.union([
    t.type({ kind: t.literal("request"), token: t.string }),
    t.type({ kind: t.literal("response"), microsoftID: t.string }),
    t.type({ kind: t.literal("error"), status: t.number, message: t.string }),
]);

export const integrateWithAirtableRequestBody = t.intersection([
    airtableTokens,
    t.type({
        testOnly: t.boolean,
    }),
]);

export const stripeInAppPurchaseConfirmIntentBody = t.intersection([
    appIDBody,
    t.type({
        purchaseID: t.string,
    }),
]);

const shippingAddressCodec = t.type({
    city: t.string,
    country: t.string,
    line1: t.string,
    line2: t.string,
    postalCode: t.string, // Many countries have non-numeric ZIP codes
    state: t.string,
});

export type ShippingAddress = t.TypeOf<typeof shippingAddressCodec>;

const requestedInAppPurchaseBase = t.intersection([
    appIDBody,
    t.type({
        paymentProcessor: paymentProcessorCodec,
        // FIXME: This should be specific to the paymentProcessor.
        processorPayload: t.intersection([
            t.type({
                paymentMethodID: t.string,
                idempotencyKey: t.string,
            }),
            t.partial({
                tdsRedirect: t.string,
            }),
        ]),
        billingName: t.string,
        // Be advised, some records on `staging` are missing this.
        receiptEmail: t.string,
    }),
    t.partial({
        shippingAddress: shippingAddressCodec,
        shippingPhone: t.string,
        testMode: t.boolean,
    }),
]);

const requestedInAppPurchaseItem = t.intersection([
    t.type({
        buttonID: t.string,
        productSKU: t.union([t.string, t.number]),
    }),
    t.partial({
        rowIndex: baseRowIndexCodec,
    }),
]);

export const requestedInAppPurchaseV1 = t.intersection([requestedInAppPurchaseBase, requestedInAppPurchaseItem]);

export const requestedInAppPurchaseV2 = t.intersection([
    requestedInAppPurchaseBase,
    t.type({
        version: t.literal(2),
        items: t.array(requestedInAppPurchaseItem),
    }),
]);

export const requestedInAppPurchase = t.union([requestedInAppPurchaseV1, requestedInAppPurchaseV2]);

export const geocodeAddressesBody = t.type({
    addresses: t.readonlyArray(t.string),
});

// Why is this not just part of io-ts?
const primitiveValue = t.union([t.string, t.number, t.boolean, t.null]);

const triggerZapValues = t.partial({
    val1: primitiveValue,
    val2: primitiveValue,
    val3: primitiveValue,
    val4: primitiveValue,
    val5: primitiveValue,
    val6: primitiveValue,
    val7: primitiveValue,
    val8: primitiveValue,
    val9: primitiveValue,
    val10: primitiveValue,
    val11: primitiveValue,
    val12: primitiveValue,
});

export const triggerZapBody = t.intersection([
    t.type({
        zapID: t.string,
        appID: t.string,
    }),
    triggerZapValues,
]);

const appSnapshotURLs = t.partial({
    dataSnapshot: t.string,
    publishedAppSnapshot: t.string,
    nativeTableSnapshots: t.record(t.string, t.string),
});

export const getAppSnapshotRequestBody = appIDBody;
export const getAppSnapshotResponseBody = t.intersection([
    appIDBody,
    appSnapshotURLs,
    t.type({
        dataSnapshot: t.string,
    }),
    t.partial({
        schema: typeSchemaCodec,
    }),
]);

export const ensureDataLivelinessBody = t.intersection([
    appIDBody,
    t.type({ editor: t.boolean }),
    t.partial({ deviceID: t.string, standalone: t.boolean, builder: t.boolean }),
]);

export const listStripeSubscriptionsBody = t.partial({
    finishSessions: t.array(t.string),
    cancelApps: t.array(t.string),
    transferSubscription: t.type({
        from: t.string,
        to: t.string,
    }),
    uncancelApps: t.array(t.string),
});

export const getPreviewAsUserBody = t.intersection([
    appIDBody,
    t.type({
        appUserEmail: t.string,
    }),
]);

const uploadComponentKind = t.union([
    t.literal("image-picker"),
    t.literal("image"),
    t.literal("file-picker"),
    t.literal("signature"),
    t.literal("user-profile"),
    t.literal("drop-image"),
    t.literal("data-editor"),
]);
export const uploadAppFileV2Body = t.union([
    t.intersection([
        appIDBody,
        t.type({
            operation: t.literal("begin"),
            contentType: t.string,
            contentLength: t.number,
            origin: t.string,
        }),
        t.partial({
            proposedExtension: t.string,
            component: uploadComponentKind,
            deviceID: t.string,
        }),
    ]),
    t.type({ operation: t.literal("complete"), uploadID: t.string }),
    t.type({
        operation: t.literal("report-failure"),
        uploadID: t.string,
        permanent: t.boolean,
        kind: t.string,
        message: t.string,
    }),
]);

export const sendAppFeedbackBody = t.intersection([
    appIDBody,
    t.type({
        from: t.string,
        message: t.string,
    }),
]);

export const reportAppBody = t.intersection([
    appIDBody,
    t.type({
        from: t.string,
        message: t.string,
        reason: t.string,
    }),
]);

export const testIframeEmbeddableBody = t.type({
    url: t.string,
    referer: t.string,
});

export const inviteUserToTransferAppBody = t.intersection([
    appIDBody,
    t.type({
        recipientEmail: t.string,
    }),
    t.partial({
        withCopy: t.boolean,
    }),
]);

export const makeSupportCodeForAppBody = appIDBody;

export const accessSupportCodeBody = t.type({
    code: t.string,
});

// This is used during promo code application
export enum CapturePromoCodeResult {
    Ok = 0,
    InvalidCode = 1,
    ExpiredCode = 2,
    OwnerIneligible = 3,
}

export const applyPromoCodeBody = t.type({
    ownerID: t.string,
    promoCode: t.string,
    subscriptionType: t.union([t.literal(SubscriptionType.Monthly), t.literal(SubscriptionType.Annual)]),
});

export const deliverEmailFromActionBody = t.intersection([
    appIDBody,
    t.type({
        recipients: t.readonlyArray(t.string),
        subject: t.string,
        body: t.string,
    }),
    t.partial({
        replyTo: t.string,
    }),
]);

export const setAdditionalBillingBody = t.intersection([
    t.partial({
        companyName: t.string,
        additionalInfo: t.string,
    }),
    t.type({
        ownerID: t.string,
    }),
]);

// https://stripe.com/docs/api/customer_tax_ids/list
export const setTaxBody = t.type({
    ownerID: t.string,
    type: t.string,
    value: t.string,
});

export const setAppIsFavoriteBody = t.type({
    organizationID: t.string,
    appID: t.string,
    isFavorite: t.boolean,
});

export const getTaxBody = t.type({
    ownerID: t.string,
});

export const deleteTaxBody = t.intersection([
    t.partial({
        taxID: t.string,
    }),
    t.type({
        ownerID: t.string,
    }),
]);
export const exportAppDataBody = appIDBody;

export const requestDownloadLinkForExportBody = t.type({
    exportID: t.string,
});

export const deleteAppUserForAppBody = t.intersection([
    appIDBody,
    t.type({
        appUserEmail: t.string,
    }),
]);

export const requestDataExportBody = t.type({
    exportType: t.string,
    additionalInfo: t.string,
});

export const appBeaconBody = t.intersection([
    appIDBody,
    t.type({
        standalone: t.union([t.literal("true"), t.literal("false")]),
    }),
    t.partial({
        deviceID: t.string,
    }),
]);
const imageMedium = t.type({
    kind: t.literal("image"),
    name: t.string,
    url: t.string,
});
export type ImageMedium = t.TypeOf<typeof imageMedium>;

export const addToMediaLibraryBody = imageMedium;

const webhookValue = t.union([
    t.type({
        type: t.literal("number"),
        value: t.number,
    }),
    t.type({
        type: t.literal("string"),
        value: t.string,
    }),
    t.type({
        type: t.literal("boolean"),
        value: t.boolean,
    }),
    t.type({
        type: t.literal("date-time"),
        value: t.string,
    }),
]);
const webhookValues = t.record(t.string, webhookValue);
export type WebhookValue = t.TypeOf<typeof webhookValue>;
export type WebhookValues = t.TypeOf<typeof webhookValues>;

const webhookBody = t.intersection([
    appIDBody,
    t.type({
        webhookID: t.string,
        params: webhookValues,
    }),
]);
export const triggerAppWebhookActionBody = webhookBody;
export const callAPIColumnBody = webhookBody;

export const apiColumnResponseBody = webhookValue;

export const addWebhookBody = t.intersection([
    appIDBody,
    t.type({
        name: t.string,
        url: t.string,
    }),
]);

const appSettingsRequired = {
    title: t.string,
    iconImage: iconImageCodec,
    theme: baseThemeCodec,
};
const appSettingsOptional = {
    author: t.string,
    description: t.string,
    authentication: appAuthenticationCodec,
};

const appSettings = t.intersection([t.type(appSettingsRequired), t.partial(appSettingsOptional)]);
export type AppSettings = t.TypeOf<typeof appSettings>;

const appSettingsOverrides = t.partial({ ...appSettingsRequired, ...appSettingsOptional });
export type AppSettingsOverride = t.TypeOf<typeof appSettingsOverrides>;

const pageTemplateKind = t.union([t.literal("portal"), t.literal("directory"), t.literal("blank"), t.literal("form")]);
export type PageTemplateKind = t.TypeOf<typeof pageTemplateKind>;

export const newGlideTableAppBody = t.intersection([
    newAppSurveyData,
    t.type({
        overrides: appSettingsOverrides,
    }),
    t.partial({
        ownerID: t.string,
        appKind: appKindCodec,
        template: pageTemplateKind,
    }),
]);

const airtableUserForm = t.type({
    baseID: t.string,
});

const dataSourceDetails = t.union([
    t.type({
        airtable: airtableUserForm,
    }),
    t.type({
        "excel-online": t.type({
            microsoftID: t.string,
            workbookID: t.string,
        }),
    }),
]);

export const newAppBody = t.intersection([
    newAppSurveyData,
    dataSourceDetails,
    t.partial({
        ownerID: t.string,
        appKind: appKindCodec,
    }),
]);

const createAndSendAuthLinkBodyRequired = {
    emailAddress: t.string,
};
const createAndSendAuthLinkBodyOptional = {
    targetURL: t.string,
};
export const createAndSendAuthLinkBody = t.intersection([
    t.type(createAndSendAuthLinkBodyRequired),
    t.partial(createAndSendAuthLinkBodyOptional),
]);

export const sendEmailVerificationBody = t.type({
    emailAddress: t.string,
});

const storeChurnSurveyBodyRequired = {
    appID: t.string,
    reason: t.string,
};

const storeChurnSurveyBodyOptional = {
    explanation: t.string,
};

export const storeChurnSurveyBody = t.intersection([
    t.type(storeChurnSurveyBodyRequired),
    t.partial(storeChurnSurveyBodyOptional),
]);

const futureDataSource = t.union([
    t.literal("AirTable"),
    t.literal("MySQL"),
    t.literal("Excel"),
    t.literal("CSV"),
    t.literal("Salesforce"),
    t.literal("BigQuery"),
    t.literal("Redshift"),
    t.literal("GraphQL"),
    t.literal("Workday"),
]);

export const requestReloadRequestBody = appIDBody;

const mutationRowIndex = t.union([t.type({ rowIndex: t.number }), t.type({ rowID: t.string })]);
export type MutationRowIndex = t.TypeOf<typeof mutationRowIndex>;
const mutationTableName = t.type({ tableName: t.string });
const mutationColumnValues = t.type({ columnValues: t.record(t.string, t.unknown) });

const addRowTableMutation = t.intersection([
    mutationTableName,
    mutationColumnValues,
    t.type({
        kind: t.literal(ActionKind.AddRowToTable),
    }),
]);

const setColumnsTableMutation = t.intersection([
    mutationTableName,
    mutationColumnValues,
    t.intersection([
        mutationRowIndex,
        t.type({
            kind: t.literal(ActionKind.SetColumnsInRow),
        }),
    ]),
]);

const deleteRowTableMutation = t.intersection([
    mutationTableName,
    t.intersection([
        mutationRowIndex,
        t.type({
            kind: t.literal(ActionKind.DeleteRow),
        }),
    ]),
]);

const tableMutation = t.union([addRowTableMutation, setColumnsTableMutation, deleteRowTableMutation]);
export type TableMutation = t.TypeOf<typeof tableMutation>;

export const mutateTablesRequestBody = t.intersection([
    appIDBody,
    t.type({
        mutations: t.readonlyArray(tableMutation),
    }),
]);

const tableQuery = mutationTableName;
export type TableQuery = t.TypeOf<typeof tableQuery>;

export const queryTablesRequestBody = t.intersection([
    appIDBody,
    t.type({
        queries: t.readonlyArray(tableQuery),
    }),
]);

const callOpenWeatherMapApiBodyRequired = {
    latitude: t.number,
    longitude: t.number,
};

const callOpenWeatherMapApiBodyOptional = {
    lang: t.string,
    units: t.union([t.literal("standard"), t.literal("metric"), t.literal("imperial")]),
};

export const callOpenWeatherMapApiBody = t.intersection([
    t.type(callOpenWeatherMapApiBodyRequired),
    t.partial(callOpenWeatherMapApiBodyOptional),
]);

const driveListItem = t.strict({
    remoteItem: t.strict({
        id: t.string,
        name: t.string,
        parentReference: t.strict({
            driveId: t.string,
        }),
        fileSystemInfo: t.union([
            t.strict({
                lastModifiedDateTime: t.string,
            }),
            t.strict({
                lastAccessedDateTime: t.string,
            }),
        ]),
    }),
});

export const deltaItem = t.strict({
    id: t.string,
    name: t.string,
    eTag: t.string,
    lastModifiedDateTime: t.string,
    parentReference: t.strict({
        id: t.string,
        driveId: t.string,
    }),
});

export const driveListItems = t.array(driveListItem);

export const deltaPlusUnknownItem = t.union([deltaItem, t.UnknownRecord]);
export const deltaPlusUnknownItems = t.array(deltaPlusUnknownItem);
export const deltaItems = t.array(deltaItem);

export const deltaItemResponse = t.intersection([
    t.strict({
        value: deltaPlusUnknownItems,
    }),
    t.partial({
        "@odata.nextLink": t.string,
        "@odata.deltaLink": t.string,
    }),
]);

export const getExcelWorkbooksResponse = t.strict({
    workbooks: driveListItems,
});

export const getExcelWorkbooksBody = t.type({
    microsoftID: t.string,
});

export const addon = t.type({
    kind: t.string,
    quantity: t.number,
    appIDs: t.array(t.string),
});

export const subscriptionAddon = t.intersection([
    t.type({
        kind: t.string,
        period: t.string,
        count: t.number,
    }),
    t.partial({
        appID: t.string,
    }),
]);

export const previewAddonChargeBody = t.type({
    ownerID: t.string,
    addons: t.array(subscriptionAddon),
});

export const getLatestPriceForAddonBody = t.type({
    addonKind: t.string,
    period: t.union([
        t.literal("annual"),
        t.literal("monthly"),
    ])
});

export const subscribeToAddonsBody = t.intersection([
    previewAddonChargeBody,
    t.partial({
        attachPaymentMethodAsDefault: t.union([t.string, t.literal(false)]),
    }),
]);

export const cancelAppPlanBody = appIDBody;

export const subscribeToPlanBody = t.intersection([
    t.type({
        ownerID: t.string,
        plan: t.string,
        period: t.string,
    }),
    t.partial({
        attachPaymentMethodAsDefault: t.union([t.string, t.literal(false)]),
    }),
]);

export const getGlideSubscriptionBody = t.type({
    ownerID: t.string,
});

export const getOwnerEminenceBody = t.type({
    ownerID: t.string,
});

export const getAppEminenceBody = appIDBody;

export const sendCustomHubspotEventBody = t.type({
    eventName: t.string,
    properties: t.record(t.string, t.union([t.string, t.number, t.boolean])),
});

export const loadBuilderActionsBody = appIDBody;

export type AcceptInviteBody = t.TypeOf<typeof acceptInviteBody>;
export type GeneratePublishedAppDataFromSheetBody = t.TypeOf<typeof generatePublishedAppDataFromSheetBody>;
export type NewAppSurveyData = t.TypeOf<typeof newAppSurveyData>;
export type GeneratePublishedAppDataFromSheetResult =
    | (t.TypeOf<typeof generatePublishedAppDataFromSheetResult> & { schema: TypeSchema })
    | string;
export type GeneratePublishedAppDataFromExcelResult =
    | (t.TypeOf<typeof generatePublishedAppDataFromExcelResult> & { schema: TypeSchema })
    | string;
export type GeneratePublishedAppDataFromDataBody = t.TypeOf<typeof generatePublishedAppDataFromDataBody>;
export type GetAIPlaygroundImages = t.TypeOf<typeof getAIPlaygroundImages>;
export type GenerateAICompletionBody = t.TypeOf<typeof generateAICompletionBody>;
export type GeneratePublishedAppDataFromDataResponse = t.TypeOf<typeof generatePublishedAppDataFromDataResponse>;
export type PrepareReplaceGoogleSheetsSourceBody = t.TypeOf<typeof prepareReplaceGoogleSheetsSourceBody>;
export type PrepareReplaceGoogleSheetsSourceResponse = t.TypeOf<typeof prepareReplaceGoogleSheetsSourceResponse>;
export type ReloadPublishedAppDataFromSheetBody = t.TypeOf<typeof reloadPublishedAppDataFromSheetBody>;
export type GenerateAppFromDescriptionBody = t.TypeOf<typeof generateAppFromDescriptionBody>;
export type GetCustomTokenForAppBody = t.TypeOf<typeof getCustomTokenForAppBody>;
export type AuthorizeUserForAppBody = t.TypeOf<typeof authorizeUserForAppBody>;
export type GetAppUserForAuthenticatedUserBody = t.TypeOf<typeof getAppUserForAuthenticatedUserBody>;
export type SendPinForEmailBody = t.TypeOf<typeof sendPinForEmailBody>;
export type GetPasswordForEmailPinBody = t.TypeOf<typeof getPasswordForEmailPinBody>;
export type GetPasswordForOAuth2TokenBody = t.TypeOf<typeof getPasswordForOAuth2TokenBody>;
export type SendShareSMSBody = t.TypeOf<typeof sendShareSMSBody>;
export type GetUnsplashImagesBody = t.TypeOf<typeof getUnsplashImagesBody>;
export type UnsplashPingDownloadBody = t.TypeOf<typeof unsplashPingDownloadBody>;
export type RegisterForPushNotificationsBody = t.TypeOf<typeof registerForPushNotificationsBody>;
export type DeleteAppBody = t.TypeOf<typeof deleteAppBody>;
export type SetOrUpdateUserBody = t.TypeOf<typeof setOrUpdateUserBody>;
export type IsEmailSignedUpBody = t.TypeOf<typeof isEmailSignedUpBody>;
export type SetAppPlanBody = t.TypeOf<typeof setAppPlanBody>;
export type SetAppPlanUnifiedBody = t.TypeOf<typeof setAppPlanUnifiedBody>;
export type GetOAuth2TokensForGoogleSheetsBody = t.TypeOf<typeof getOAuth2TokensForGoogleSheetsBody>;
export type DuplicateAppBody = t.TypeOf<typeof duplicateAppBody>;
export type DuplicateAppResponseBody = t.TypeOf<typeof duplicateAppResponseBody>;
export type SubmitTemplateBody = t.TypeOf<typeof submitTemplateBody>;
export type Range = t.TypeOf<typeof range>;
export type ReasonForGlide = t.TypeOf<typeof reasonForGlide>;
export type SelfReportedReferralSource = t.TypeOf<typeof selfReportedReferralSource>;
export type CreateOrganizationBody = t.TypeOf<typeof createOrganizationBody>;
export type SetOrgOnboardingBody = t.TypeOf<typeof setOrgOnboardingBody>;
export type CreateOrUpdateHubspotContactBody = t.TypeOf<typeof createOrUpdateHubspotContactBody>;
export type CreateOrganizationResult = t.TypeOf<typeof createOrganizationResult>;
export type DeleteOrganizationBody = t.TypeOf<typeof deleteOrganizationBody>;
export type TransferAppToOrganizationBody = t.TypeOf<typeof transferAppToOrganizationBody>;
export type InviteAppUserBody = t.TypeOf<typeof inviteAppUserBody>;
export type InviteToOrganizationBody = t.TypeOf<typeof inviteToOrganizationBody>;
export type InviteToOrganizationResult = t.TypeOf<typeof inviteToOrganizationResult>;
export type RemoveFromOrganizationBody = t.TypeOf<typeof removeFromOrganizationBody>;
export type RenameOrganizationBody = t.TypeOf<typeof renameOrganizationBody>;
export type RenameTableBody = t.TypeOf<typeof renameTableBody>;
export type ListTablesBody = t.TypeOf<typeof listTablesBody>;
export type ListTablesResult = t.TypeOf<typeof listTablesResult>;
export type GetOrganizationMembersBody = t.TypeOf<typeof getOrganizationMembersBody>;
export type GetOrganizationMembersResult = t.TypeOf<typeof getOrganizationMembersResult>;
export type GetOrganizationPersistentInviteLinkBody = t.TypeOf<typeof getOrganizationPersistentInviteLinkBody>;
export type GetOrganizationPersistentInviteLinkResult = t.TypeOf<typeof getOrganizationPersistentInviteLinkResult>;
export type GetOrganizationBillingBody = t.TypeOf<typeof getOrganizationBillingBody>;
export type GetOrganizationBillingResult = t.TypeOf<typeof getOrganizationBillingResult>;
export type SetEmailOwnersColumnsBody = t.TypeOf<typeof setEmailOwnersColumnsBody>;
export type ModifySyntheticColumnsBody = t.TypeOf<typeof modifySyntheticColumnsBody>;
export type MoveTableInSchemaBody = t.TypeOf<typeof moveTableInSchemaBody>;
export type MoveOrgInUserBody = t.TypeOf<typeof moveOrgInUserBody>;
export type CreateAppFromTemplateBody = t.TypeOf<typeof createAppFromTemplateBody>;
export type BroadcastNotificationToAppUsersBody = t.TypeOf<typeof broadcastNotificationToAppUsersBody>;
export type TransferAppBetweenUsersBody = t.TypeOf<typeof transferAppBetweenUsersBody>;
export type ReportGeocodesInAppBody = t.TypeOf<typeof reportGeocodesInAppBody>;
export type ReportQuotaIncreaseBody = t.TypeOf<typeof reportQuotaIncreaseBody>;
export type CheckDomainConfiguredBody = t.TypeOf<typeof checkDomainConfiguredBody>;
export type AddTableBody = t.TypeOf<typeof addTableBody>;
export type RemoveTableBody = t.TypeOf<typeof removeTableBody>;
export type DuplicateTableBody = t.TypeOf<typeof duplicateTableBody>;
export type LinkTablesBody = t.TypeOf<typeof linkTablesBody>;
export type SyncTablesBody = t.TypeOf<typeof syncTablesBody>;
export type DeleteTableBody = t.TypeOf<typeof deleteTableBody>;
export type SetCustomDomainBody = t.TypeOf<typeof setCustomDomainBody>;
export type SetShortNameBody = t.TypeOf<typeof setShortNameBody>;
export type SetPublishingActiveBody = t.TypeOf<typeof setPublishingActiveBody>;
export type SetPublishingActiveResponseBody = t.TypeOf<typeof setPublishingActiveResponseBody>;
export type DiscardPausedAppChangesBody = t.TypeOf<typeof discardPausedAppChangesBody>;
// FIXME: This has been deprecated.
export type AcquireStripeSessionBody = t.TypeOf<typeof acquireStripeSessionBody>;
export type AcquireStripeSessionForProPurchaseBody = t.TypeOf<typeof acquireStripeSessionForProPurchaseBody>;
export type AcquireStripeSessionForTemplatePurchaseBody = t.TypeOf<typeof acquireStripeSessionForTemplatePurchaseBody>;
export type UpdateUnifiedPaymentInformationBody = t.TypeOf<typeof updateUnifiedPaymmentInformationBody>;
export type SetBoostsForOwnerBody = t.TypeOf<typeof setBoostsForOwnerBody>;
export type IntegrateWithStripeBody = t.TypeOf<typeof integrateWithStripeBody>;
export type IntegrateWithMicrosoftRequestBody = t.TypeOf<typeof integrateWithMicrosoftRequestBody>;
export type IntegrateWithAirtableRequestBody = t.TypeOf<typeof integrateWithAirtableRequestBody>;
export type StripeInAppPurchaseConfirmIntentBody = t.TypeOf<typeof stripeInAppPurchaseConfirmIntentBody>;
export type RequestedInAppPurchaseV1 = Readonly<t.TypeOf<typeof requestedInAppPurchaseV1>>;
export type RequestedInAppPurchaseV2 = Readonly<t.TypeOf<typeof requestedInAppPurchaseV2>>;
export type RequestedInAppPurchase = Readonly<t.TypeOf<typeof requestedInAppPurchase>>;
export type GeocodeAddressesBody = t.TypeOf<typeof geocodeAddressesBody>;
export type GetAppSnapshotRequestBody = t.TypeOf<typeof getAppSnapshotRequestBody>;
export type GetAppSnapshotResponseBody = t.TypeOf<typeof getAppSnapshotResponseBody>;
export type AppSnapshotURLs = t.TypeOf<typeof appSnapshotURLs>;
export type GetOrganizationPlansBody = t.TypeOf<typeof getOrganizationPlansBody>;
export type AppBeaconBody = t.TypeOf<typeof appBeaconBody>;
export type EnsureDataLivelinessBody = t.TypeOf<typeof ensureDataLivelinessBody>;
export type TriggerZapValues = t.TypeOf<typeof triggerZapValues>;
export type TriggerZapBody = t.TypeOf<typeof triggerZapBody>;
export type ListStripeSubscriptionBody = t.TypeOf<typeof listStripeSubscriptionsBody>;
export type TestIframeEmbeddableBody = t.TypeOf<typeof testIframeEmbeddableBody>;
export type GetPreviewAsUserBody = t.TypeOf<typeof getPreviewAsUserBody>;
export type CheckDomainConfiguredResult = t.TypeOf<typeof checkDomainConfiguredResult>;
export type CheckDomainRecord = t.TypeOf<typeof checkDomainRecord>;
export type UploadComponentKind = t.TypeOf<typeof uploadComponentKind>;
export type UploadAppFileV2Body = t.TypeOf<typeof uploadAppFileV2Body>;
export type SendAppFeedbackBody = t.TypeOf<typeof sendAppFeedbackBody>;
export type ReportAppBody = t.TypeOf<typeof reportAppBody>;
export type InviteUserToTransferAppBody = t.TypeOf<typeof inviteUserToTransferAppBody>;
export type MakeSupportCodeForAppBody = t.TypeOf<typeof makeSupportCodeForAppBody>;
export type AccessSupportCodeBody = t.TypeOf<typeof accessSupportCodeBody>;
export type ApplyPromoCodeBody = t.TypeOf<typeof applyPromoCodeBody>;
export type DeliverEmailFromActionBody = t.TypeOf<typeof deliverEmailFromActionBody>;
export type SetAdditionalBillingBody = t.TypeOf<typeof setAdditionalBillingBody>;
export type GetTaxBody = t.TypeOf<typeof getTaxBody>;
export type SetTaxBody = t.TypeOf<typeof setTaxBody>;
export type DeleteTaxBody = t.TypeOf<typeof deleteTaxBody>;
export type ExportAppDataBody = t.TypeOf<typeof exportAppDataBody>;
export type RequestDownloadLinkForExportBody = t.TypeOf<typeof requestDownloadLinkForExportBody>;
export type DeleteAppUserForAppBody = t.TypeOf<typeof deleteAppUserForAppBody>;
export type AddToMediaLibraryBody = t.TypeOf<typeof addToMediaLibraryBody>;
export type RequestDataExportBody = t.TypeOf<typeof requestDataExportBody>;
export type TriggerAppWebhookActionBody = t.TypeOf<typeof triggerAppWebhookActionBody>;
export type CallAPIColumnBody = t.TypeOf<typeof callAPIColumnBody>;
export type AddWebhookBody = t.TypeOf<typeof addWebhookBody>;
export type NewGlideTableAppBody = t.TypeOf<typeof newGlideTableAppBody>;
export type NewAppBody = t.TypeOf<typeof newAppBody>;
export type DataSourceDetails = t.TypeOf<typeof dataSourceDetails>;
export type AirtableUserForm = t.TypeOf<typeof airtableUserForm>;
export type CreateAndSendAuthLinkBody = t.TypeOf<typeof createAndSendAuthLinkBody>;
export type SendEmailVerificationBody = t.TypeOf<typeof sendEmailVerificationBody>;
export type StoreChurnSurveyBody = t.TypeOf<typeof storeChurnSurveyBody>;
export type SetAppIsFavoriteBody = t.TypeOf<typeof setAppIsFavoriteBody>;
export type FinishOnboardingBody = t.TypeOf<typeof finishOnboardingBody>;
export type FutureDataSource = t.TypeOf<typeof futureDataSource>;
export type RequestReloadRequestBody = t.TypeOf<typeof requestReloadRequestBody>;
export type MutateTablesRequestBody = t.TypeOf<typeof mutateTablesRequestBody>;
export type QueryTablesRequestBody = t.TypeOf<typeof queryTablesRequestBody>;
export type CallOpenWeatherMapApiBody = t.TypeOf<typeof callOpenWeatherMapApiBody>;
export type GetExcelWorkbooksBody = t.TypeOf<typeof getExcelWorkbooksBody>;
export type GetExcelWorkbooksResponse = t.TypeOf<typeof getExcelWorkbooksResponse>;
export type DriveListItems = t.TypeOf<typeof driveListItems>;
export type DeltaItems = t.TypeOf<typeof deltaItems>;
export type DeltaItem = t.TypeOf<typeof deltaItem>;
export type SubscriptionAddon = t.TypeOf<typeof subscriptionAddon>;
export type PreviewAddonChargeBody = t.TypeOf<typeof previewAddonChargeBody>;
export type LatestPriceForAddonBody = t.TypeOf<typeof getLatestPriceForAddonBody>;
export type SubscribeToAddonsBody = t.TypeOf<typeof subscribeToAddonsBody>;
export type CancelAppPlanBody = t.TypeOf<typeof cancelAppPlanBody>;
export type SubscribeToPlanBody = t.TypeOf<typeof subscribeToPlanBody>;
export type GetGlideSubscriptionBody = t.TypeOf<typeof getGlideSubscriptionBody>;
export type GetOwnerEminenceBody = t.TypeOf<typeof getOwnerEminenceBody>;
export type GetAppEminenceBody = t.TypeOf<typeof getAppEminenceBody>;
export type CustomHubspotEventBody = t.TypeOf<typeof sendCustomHubspotEventBody>;
export type LoadBuilderActionsBody = t.TypeOf<typeof loadBuilderActionsBody>;

export function isRequestedInAppPurchaseV1(purchase: RequestedInAppPurchase): purchase is RequestedInAppPurchaseV1 {
    return !hasOwnProperty(purchase, "version");
}

function convertRequestedInAppPurchaseV1toV2(purchase: RequestedInAppPurchaseV1): RequestedInAppPurchaseV2 {
    const {
        appID,
        buttonID,
        paymentProcessor,
        processorPayload,
        billingName,
        receiptEmail,
        shippingAddress,
        productSKU,
        rowIndex,
    } = purchase;
    return {
        version: 2,
        appID,
        paymentProcessor,
        processorPayload,
        billingName,
        receiptEmail,
        shippingAddress,
        items: [{ buttonID, productSKU, rowIndex }],
    };
}

export function ensureRequestedInAppPurchaseV2(purchase: RequestedInAppPurchase): RequestedInAppPurchaseV2 {
    if (isRequestedInAppPurchaseV1(purchase)) {
        return convertRequestedInAppPurchaseV1toV2(purchase);
    } else {
        return purchase;
    }
}

export type StripeSubscriptionRepresentation = Readonly<{
    subscriptionID: string;
    current: boolean;
    planSKU: string;
    appID: string | undefined;
    checkoutSessionID: string;
    cardInfo: StripeCardInfo | undefined;
    since: StripeDate;
    cancelsAt: StripeDate | undefined;
    nextInvoice: StripeDate | undefined;
    nextTotal: number | undefined;
    currency: string;
}>;
